/**
 * Detaljside for credentials importert fra eksterne kilder (i første omgang
 * Vitnemålsportalen via EMREX). Viser hele hierarkiet:
 *   - Toppnivå: gradnavn, institusjon, ECTS, oppnådd dato
 *   - Liste over alle delemner: kode, tittel, termin, ECTS, karakter, beskrivelser
 *   - Eventuell karakterfordeling per emne
 */

import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, GraduationCap, Calendar, MapPin, ExternalLink, ChevronRight, Hash, BookOpen, FileText, Shield, Loader2, Trash2 } from 'lucide-react';
import { checkSessionExpired } from '../lib/api';
import type { Certificate } from '../types';

interface CourseEntry {
  code?: string | null;
  title?: { nb?: string | null; en?: string | null };
  type?: string | null;
  iscedCode?: string | null;
  subjectArea?: string | null;
  url?: string | null;
  identifiers?: Record<string, string>;
  descriptions?: Array<{ lang?: string | null; content?: string | null }>;
  instance?: {
    startDate?: string | null;
    endDate?: string | null;
    awardingDate?: string | null;
    term?: { titles?: Record<string, string>; start?: string | null; end?: string | null } | null;
    credit?: { scheme?: string; value?: number; level?: string | null } | null;
    grade?: string | null;
    resultDistribution?: Array<{ label: string; count: number }> | null;
    engagementHours?: number | null;
  };
  subParts?: CourseEntry[];
}

interface FullCertificate extends Certificate {
  edc_credential?: {
    issuer?: { preferredName?: { nb?: string; en?: string }; homepage?: string };
    credentialSubject?: {
      hasClaim?: Array<{
        title?: { nb?: string; en?: string };
        ECTSCreditPoints?: number;
        specifiedBy?: {
          'kompetanseforbundet:academicLevel'?: string;
          'kompetanseforbundet:learningType'?: string;
        };
        assessment?: { grade?: { noteLiteral?: { nb?: string; en?: string } } };
        awardedBy?: { awardingDate?: string };
        'kompetanseforbundet:courses'?: CourseEntry[];
      }>;
    };
  };
}

function formatDate(dateStr?: string | null): string {
  if (!dateStr) return '–';
  try {
    return new Date(dateStr).toLocaleDateString('nb-NO', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return dateStr; }
}

function decodeHtmlEntities(s: string): string {
  // VP-beskrivelser er ofte HTML-encodet (&lt;br&gt; osv.)
  return s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ');
}

function CourseCard({ course, level = 0 }: { course: CourseEntry; level?: number }) {
  // Standard: kollapset (sammenslått linje). Klikk for å utvide.
  const [expanded, setExpanded] = useState(false);
  const titleNb = course.title?.nb || course.title?.en || 'Uten tittel';
  const inst = course.instance;
  const term = inst?.term?.titles?.nb || inst?.term?.titles?.en || inst?.term?.titles?.und;
  const ects = inst?.credit?.value;
  const grade = inst?.grade;

  const hasDescriptions = (course.descriptions?.length || 0) > 0;
  const hasSubParts = (course.subParts?.length || 0) > 0;

  return (
    <div
      className={`border rounded ${level === 0 ? 'border-blue-200 bg-white' : 'border-gray-200 bg-gray-50'} overflow-hidden`}
      style={{ marginLeft: level * 16 }}
    >
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-3"
      >
        <ChevronRight
          className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${expanded ? 'rotate-90' : ''}`}
        />
        <div className="flex-1 min-w-0 flex items-center gap-2 text-sm">
          {course.code && (
            <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded text-gray-700 flex-shrink-0">
              {course.code}
            </span>
          )}
          <span className="font-medium text-gray-900 truncate">{titleNb}</span>
        </div>
        <div className="flex items-center gap-3 text-sm text-gray-600 flex-shrink-0">
          {ects != null && <span><span className="font-semibold text-gray-900">{ects}</span> stp</span>}
          {grade && (
            <span className="text-xs">
              Karakter: <span className="font-bold text-gray-900">{grade}</span>
            </span>
          )}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-200 px-4 pb-4 pt-3 space-y-3 bg-white">
          {/* Meta-info: termin + fagfelt */}
          {(term || course.subjectArea) && (
            <div className="text-xs text-gray-600 flex flex-wrap gap-x-4 gap-y-1">
              {term && <div><span className="text-gray-500">Termin:</span> {term}</div>}
              {course.subjectArea && (
                <div><span className="text-gray-500">Fagfelt:</span> {course.subjectArea}</div>
              )}
            </div>
          )}

          {/* URL til emneside */}
          {course.url && (
            <div className="text-xs">
              <a
                href={course.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline inline-flex items-center gap-1"
              >
                Emneside hos institusjonen
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          )}

          {/* Beskrivelser med språk */}
          {hasDescriptions && (
            <div className="space-y-3">
              {course.descriptions!.map((d, i) => (
                <div key={i} className="text-sm text-gray-700">
                  {d.lang && (
                    <div className="text-xs text-gray-500 mb-1">
                      Språk: {d.lang === 'no' || d.lang === 'nb' ? 'Norsk' : d.lang === 'en' ? 'English' : d.lang}
                    </div>
                  )}
                  <div
                    className="prose prose-sm max-w-none [&>p]:mb-2 [&>strong]:font-semibold [&>br]:block"
                    dangerouslySetInnerHTML={{ __html: decodeHtmlEntities(d.content || '') }}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Sub-emner (rekursiv) */}
          {hasSubParts && (
            <div className="space-y-2 pt-2 border-t border-gray-100">
              <div className="text-xs text-gray-500">Delemner ({course.subParts!.length})</div>
              {course.subParts!.map((sub, i) => (
                <CourseCard key={sub.code || i} course={sub} level={level + 1} />
              ))}
            </div>
          )}

          {!hasDescriptions && !hasSubParts && (
            <div className="text-xs text-gray-500 italic">Ingen ytterligere detaljer</div>
          )}
        </div>
      )}
    </div>
  );
}

export function ExternalCredentialDetailPage() {
  const { credentialId } = useParams<{ credentialId: string }>();
  const navigate = useNavigate();
  const [cert, setCert] = useState<FullCertificate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  async function handleDelete() {
    if (!cert) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const res = await fetch(`/api/candidate/vp/imports/${cert.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Kunne ikke slette');
      navigate('/dashboard');
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'Ukjent feil');
      setDeleting(false);
    }
  }

  useEffect(() => {
    async function fetchCert() {
      try {
        const res = await fetch('/api/candidate/certificates', { credentials: 'include' });
        await checkSessionExpired(res);
        if (res.ok) {
          const data = await res.json();
          const found = (data.certificates || []).find((c: FullCertificate) => c.id === credentialId);
          if (found) setCert(found);
          else setError('Vitnemål ikke funnet');
        } else {
          setError('Kunne ikke hente vitnemål');
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Nettverksfeil');
      } finally {
        setLoading(false);
      }
    }
    fetchCert();
  }, [credentialId]);

  if (loading) {
    return (
      <div className="text-center py-12">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto" />
      </div>
    );
  }

  if (error || !cert) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error || 'Vitnemål ikke funnet'}</p>
        <Link to="/dashboard" className="text-blue-600 hover:underline inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Tilbake til Mine attester
        </Link>
      </div>
    );
  }

  const cred = cert.edc_credential;
  const claim = cred?.credentialSubject?.hasClaim?.[0];
  const courses = claim?.['kompetanseforbundet:courses'] || [];
  const issuerName = cred?.issuer?.preferredName?.nb || cred?.issuer?.preferredName?.en;
  const academicLevel = claim?.specifiedBy?.['kompetanseforbundet:academicLevel'];
  const niceLevel: Record<string, string> = {
    Bachelor: 'Bachelorgrad',
    Master: 'Mastergrad',
    PhD: 'Doktorgrad'
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Link to="/dashboard" className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Tilbake til Mine attester
        </Link>
        <button
          type="button"
          onClick={() => setConfirmingDelete(true)}
          className="text-xs text-red-600 hover:text-red-800 hover:bg-red-50 px-3 py-1.5 rounded-md font-medium border border-red-200 inline-flex items-center gap-1"
          title="Fjern dette vitnemålet fra lommeboken"
        >
          <Trash2 className="w-3.5 h-3.5" />
          Fjern fra lommebok
        </button>
      </div>

      {confirmingDelete && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => !deleting && setConfirmingDelete(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="bg-red-100 p-2 rounded-full flex-shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Fjern vitnemål fra lommeboken?</h3>
                <p className="text-sm text-gray-600">
                  "{cert.title}" vil bli fjernet. Du kan importere det på nytt fra Vitnemålsportalen senere hvis du ombestemmer deg.
                </p>
              </div>
            </div>
            {deleteError && (
              <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                {deleteError}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmingDelete(false)}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50"
              >
                Avbryt
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
              >
                {deleting ? 'Fjerner…' : 'Ja, fjern'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start gap-4">
          <div className="bg-blue-600 text-white p-3 rounded-lg">
            <GraduationCap className="w-7 h-7" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 text-xs text-blue-800 mb-1">
              <Shield className="w-3.5 h-3.5" />
              <span className="font-medium">Vitnemålsportalen</span>
              {academicLevel && <span>· {niceLevel[academicLevel] || academicLevel}</span>}
            </div>
            <h1 className="text-2xl font-bold text-gray-900">{cert.title}</h1>
            <p className="text-gray-700 mt-1 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-gray-500" />
              {issuerName}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-blue-200">
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">Studiepoeng</div>
            <div className="font-semibold text-gray-900">{cert.ects_credits ?? '–'} ECTS</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">Karakter</div>
            <div className="font-semibold text-gray-900">{cert.assessment_result?.grade || 'Bestått'}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">Tildelt</div>
            <div className="font-semibold text-gray-900 flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-gray-400" />
              {formatDate(cert.course_completion_date)}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">Antall emner</div>
            <div className="font-semibold text-gray-900 flex items-center gap-1.5">
              <BookOpen className="w-3.5 h-3.5 text-gray-400" />
              {courses.length}
            </div>
          </div>
        </div>
      </div>

      {/* Emner */}
      {courses.length > 0 ? (
        <div>
          <h2 className="text-lg font-bold text-gray-900 mb-3">Emner i graden</h2>
          <div className="space-y-2">
            {courses.map((course, i) => (
              <CourseCard key={course.code || i} course={course} level={0} />
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-600">Ingen emnedetaljer tilgjengelig for dette vitnemålet.</p>
          <p className="text-xs text-gray-500 mt-1">
            Vitnemålsportalen kan ha eldre poster uten emneliste.
          </p>
        </div>
      )}

      {/* Footer-meta */}
      <div className="text-xs text-gray-500 pt-4 border-t border-gray-200 space-y-1">
        <div className="flex items-center gap-2">
          <Hash className="w-3.5 h-3.5" />
          Sertifikat-nr: <span className="font-mono">{cert.certificate_number}</span>
        </div>
        <div className="flex items-center gap-2">
          <FileText className="w-3.5 h-3.5" />
          Importert {formatDate(cert.external_imported_at)} fra Vitnemålsportalen ·
          Signert digitalt av Sikt
        </div>
      </div>
    </div>
  );
}
